import { CSSProperties, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  IconExternalLink,
  IconInfoCircle,
  IconMail,
  IconPhone,
} from '@tabler/icons-react'
import Link from 'next/link'
import styled from 'styled-components'

import { useBusinessNumberInput } from '../form-components/BusinessNumberInput'

import { Popover } from './BUI/Popover'
import { Text } from './BUI/Text'
import { DozerLoading } from './DozerLoading'
import { Flexbox } from './Flexbox'
import { ProjectDisplay } from './ProjectDisplay'

import { useLocale } from '@/context/localeContext'
import { GET_ADMIN_USER_INFO } from '@/modules/adminUsers/graphQL'
import {
  GET_CONTACT_INFO,
  GET_SUBBIE_INFO,
} from '@/modules/subcontractors/graphQL'
import { getPreferredContacts } from '@/modules/subcontractors/helpers'
import { GET_VISITOR_INFO, GET_WORKER_INFO } from '@/modules/workers/graphQL'
import {
  convertEnumToString,
  getAdminUserName,
  getWorkerName,
  routes,
} from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

import {
  AdminUserInfoUiQuery,
  AdminUserInfoUiQueryVariables,
  ContactInfoUiQuery,
  ContactInfoUiQueryVariables,
  SubbieInfoUiQuery,
  SubbieInfoUiQueryVariables,
  VisitorInfoUiQuery,
  VisitorInfoUiQueryVariables,
  WorkerInfoUiQuery,
  WorkerInfoUiQueryVariables,
} from 'types/api.graphql'
import { AssigneeLike } from 'types/vagueTypes'

const Wrapper = styled.div`
  padding: 1rem 1.5rem;
`

type Contact = {
  name?: string
  email?: string
  mobile?: string
}

type Info = {
  link?: string
  name: string
  subtitle?: React.ReactNode
  contacts?: Contact[]
}

const convertWorkerToInfo = (worker: WorkerInfoUiQuery['worker']): Info => {
  if (!worker?.id) return null

  return {
    link: routes.workers.view(worker.id),
    name: getWorkerName(worker),
    subtitle: worker.trade,
    contacts: [{ email: worker.user.email, mobile: worker.mobile }],
  }
}

const convertVisitorToInfo = (visitor: VisitorInfoUiQuery['visitor']): Info => {
  if (!visitor?.id) return null

  return {
    name: getWorkerName(visitor),
    contacts: [{ email: visitor.user.email, mobile: visitor.mobile }],
  }
}

const convertSubbieToInfo = (
  subbie: SubbieInfoUiQuery['subcontractor'],
  formatter: (value: string) => string,
  projectId?: string
): Info => {
  if (!subbie?.id) return null

  return {
    link: routes.subcontractors.view(subbie.id),
    name: subbie.company.name,
    subtitle: formatter(subbie.company.abn),
    contacts: getPreferredContacts(subbie.contacts, projectId).map((c) => ({
      name: c.name,
      email: c.email,
      mobile: c.number,
    })),
  }
}

const convertContactToInfo = (contact: ContactInfoUiQuery['contact']): Info => {
  if (!contact?.id) return null

  return {
    link: routes.subcontractors.view(contact.subcontractor.id),
    name: contact.name,
    subtitle: contact.subcontractor?.company?.name,
    contacts: [{ email: contact.email, mobile: contact.number }],
  }
}

const convertAdminToInfo = (admin: AdminUserInfoUiQuery['adminUser']): Info => {
  if (!admin?.id) return null

  return {
    name: getAdminUserName(admin),
    subtitle: <ProjectDisplay projects={admin.projects} />,
    contacts: [{ email: admin.user.email, mobile: admin.mobile }],
  }
}

type WorkerProps = {
  mode: 'WORKER'
  id: string
  projectId?: string // not used
  children: React.ReactNode
}

type VisitorProps = {
  mode: 'VISITOR'
  id: string
  projectId?: string // not used
  children: React.ReactNode
}

type SubbieProps = {
  mode: 'SUBCONTRACTOR'
  id: string
  projectId: string // used to get preferred contacts
  children: React.ReactNode
}

type ContactProps = {
  mode: 'CONTACT'
  id: string
  projectId?: string // not used
  children: React.ReactNode
}

type AdminUserProps = {
  mode: 'ADMIN_USER'
  id: string
  projectId?: string // not used
  children: React.ReactNode
}

type ManualUserProps = {
  mode: 'MANUAL_USER'
  id?: string
  projectId?: string // not used
  userInfo: Info
  children: React.ReactNode
}

type BaseProps = {
  noLinkStyles?: boolean
  noIcon?: boolean
  wrapperStyles?: CSSProperties
}

type Props = (
  | WorkerProps
  | VisitorProps
  | SubbieProps
  | ContactProps
  | AdminUserProps
  | ManualUserProps
) &
  BaseProps

export const UserInfoOverlay: React.FC<Props> = ({
  mode,
  id,
  projectId,
  noLinkStyles,
  noIcon,
  wrapperStyles,
  children,
  ...rest
}) => {
  const [open, setOpen] = useState(false)

  const { t } = useLocale()
  const { formatBusinessNumber } = useBusinessNumberInput()

  const { data: workerData, loading: workerLoading } = useQuery<
    WorkerInfoUiQuery,
    WorkerInfoUiQueryVariables
  >(GET_WORKER_INFO, {
    variables: { id },
    skip: mode !== 'WORKER' || !open,
  })

  const { data: visitorData, loading: visitorLoading } = useQuery<
    VisitorInfoUiQuery,
    VisitorInfoUiQueryVariables
  >(GET_VISITOR_INFO, {
    variables: { id },
    skip: mode !== 'VISITOR' || !open,
  })

  const { data: subbieData, loading: subbieLoading } = useQuery<
    SubbieInfoUiQuery,
    SubbieInfoUiQueryVariables
  >(GET_SUBBIE_INFO, {
    variables: { id },
    skip: mode !== 'SUBCONTRACTOR' || !open,
  })

  const { data: contactData, loading: contactLoading } = useQuery<
    ContactInfoUiQuery,
    ContactInfoUiQueryVariables
  >(GET_CONTACT_INFO, { variables: { id }, skip: mode !== 'CONTACT' || !open })

  const { data: adminData, loading: adminLoading } = useQuery<
    AdminUserInfoUiQuery,
    AdminUserInfoUiQueryVariables
  >(GET_ADMIN_USER_INFO, {
    variables: { id },
    skip: mode !== 'ADMIN_USER' || !open,
  })

  const ChildrenWrapper = ({ children }) =>
    noLinkStyles ? children : <Text link>{children}</Text>

  const info = useMemo(() => {
    if (workerData) {
      return convertWorkerToInfo(workerData.worker)
    }

    if (visitorData) {
      return convertVisitorToInfo(visitorData.visitor)
    }

    if (subbieData) {
      return convertSubbieToInfo(
        subbieData.subcontractor,
        formatBusinessNumber,
        projectId
      )
    }

    if (contactData) {
      return convertContactToInfo(contactData.contact)
    }

    if (adminData) {
      return convertAdminToInfo(adminData.adminUser)
    }

    if (mode === 'MANUAL_USER') {
      return (rest as ManualUserProps).userInfo
    }

    return null
  }, [
    workerData,
    visitorData,
    subbieData,
    contactData,
    adminData,
    mode,
    formatBusinessNumber,
    projectId,
    rest,
  ])

  const isLoading =
    !info ||
    workerLoading ||
    visitorLoading ||
    subbieLoading ||
    contactLoading ||
    adminLoading

  const modeText = useMemo(() => {
    if (mode === 'MANUAL_USER') return t('subcontractor:labels.contact')
    if (mode === 'SUBCONTRACTOR') return t('subcontractor:singular')
    return convertEnumToString(mode)
  }, [mode, t])

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      showArrow={false}
      trigger={['click']}
      content={
        <Wrapper>
          {isLoading && <DozerLoading relative loading noBackground />}
          <Flexbox flexDirection="column" gap="0.25rem">
            <Flexbox
              justifyContent="space-between"
              alignItems="center"
              gap="0.5rem"
              mb="0.25rem"
            >
              <Text variant="caption02" secondary="_400">
                {modeText}
              </Text>
              {info?.link && (
                <Link href={info.link}>
                  <Flexbox alignItems="center" gap="0.5rem">
                    <IconExternalLink size={14} /> Open
                  </Flexbox>
                </Link>
              )}
            </Flexbox>
            <Text variant="label01" strong>
              {info?.name}
            </Text>
            {info?.subtitle && <Text variant="label02">{info.subtitle}</Text>}

            {info?.contacts?.map(({ name, email, mobile }) => (
              <Flexbox key={email + mobile} flexDirection="column" mt="0.5rem">
                {name && <Text variant="caption01">{name}</Text>}
                {mobile && (
                  <Flexbox gap="0.5rem" alignItems="center">
                    <IconPhone size={14} />
                    <a href="tel:{mobile}">
                      <Text variant="label02">{mobile}</Text>
                    </a>
                  </Flexbox>
                )}
                {email && (
                  <Flexbox gap="0.5rem" alignItems="center">
                    <IconMail size={14} />
                    <a href={`mailto:${email}`}>
                      <Text variant="label02">{email}</Text>
                    </a>
                  </Flexbox>
                )}
              </Flexbox>
            ))}
          </Flexbox>
        </Wrapper>
      }
    >
      <Flexbox
        flex="inline-flex"
        gap="0.25rem"
        alignItems="center"
        style={wrapperStyles}
      >
        <ChildrenWrapper>{children}</ChildrenWrapper>
        {!noIcon && (
          <IconInfoCircle
            size={14}
            color={buildPassTheme.colour.link.base}
            style={{ cursor: 'pointer' }}
          />
        )}
      </Flexbox>
    </Popover>
  )
}

type AssigneeProps = BaseProps & {
  assignee: AssigneeLike
  projectId?: string
  children: React.ReactNode
}

export const AssigneeUserInfoOverlay: React.FC<AssigneeProps> = ({
  assignee,
  projectId,
  children,
  ...rest
}) => {
  if (assignee.worker?.id) {
    return (
      <UserInfoOverlay mode="WORKER" id={assignee.worker.id} {...rest}>
        {children}
      </UserInfoOverlay>
    )
  }

  if (assignee.adminUser?.id) {
    return (
      <UserInfoOverlay mode="ADMIN_USER" id={assignee.adminUser.id} {...rest}>
        {children}
      </UserInfoOverlay>
    )
  }

  if (assignee.subcontractor?.id) {
    return (
      <UserInfoOverlay
        mode="SUBCONTRACTOR"
        id={assignee.subcontractor.id}
        projectId={projectId}
        {...rest}
      >
        {children}
      </UserInfoOverlay>
    )
  }
}
